/* eslint-disable import/no-internal-modules -- reuses the events list's query helpers and types */
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { Event } from '@livecontrol/scheduler/model';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { store, useEventsSocket } from '../../store';
import type { EventType, EventVisibility } from '../events/graphql/type';
import { transformFiltersToVariables } from '../events/graphql/utils';

/*
 * Only database-backed fields. `getEvents` calls the asset service only when one of `muxStreamUrl`,
 * `has_asset`, `hidden` or `backgroundUrl` is selected, so none of them are here. Contacts and
 * `is_first_event` are left out too: each is an extra database query the resolver skips when unselected.
 */
const QUERY = gql`
  query GetLiveLiteEvents($input: GetEventsInput!) {
    getEvents(input: $input) {
      page
      limit
      has_more
      total
      result {
        eid
        name
        startTime
        endTime
        eventType
        isLive
        status
        visibility
        high_priority
        location_name
        location_secondary_id
        receive_iso_recordings
        audio_cue_sheet_url
        operator_name
        stream_facebook
        stream_rtmp
        stream_youtube
        stream_zoom
        stream_live
        client {
          cid
          organizationName
        }
      }
    }
  }
`;

export interface LiveLiteEvent {
  eid: string;
  name?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  eventType?: EventType | null;
  isLive?: boolean | null;
  status?: string | null;
  visibility?: EventVisibility;
  high_priority?: boolean | null;
  /* Both come from the location join the resolver already does — no extra query. */
  location_name?: string | null;
  location_secondary_id?: string | null;
  receive_iso_recordings?: boolean | null;
  audio_cue_sheet_url?: string | null;
  operator_name?: string | null;
  stream_facebook?: boolean | null;
  stream_rtmp?: boolean | null;
  stream_youtube?: boolean | null;
  stream_zoom?: boolean | null;
  stream_live?: boolean | null;
  client?: { cid?: string | null; organizationName?: string | null } | null;
}

interface LiveLiteEventsQuery {
  getEvents?: {
    /* Nullable in the schema, and it is what says which page these rows belong to. */
    page?: number | null;
    limit?: number | null;
    has_more?: boolean | null;
    /* Total matching events, so the footer can say which page of how many. */
    total?: number | null;
    result: (LiveLiteEvent | null)[];
  } | null;
}

interface LiveLiteEventsVariables {
  input: ReturnType<typeof transformFiltersToVariables>['input'] & {
    status: number;
    page: number;
    ids?: number[];
    ignore_pagination?: boolean;
  };
}

export interface LiveLiteFilters {
  search?: string;
  eventType?: EventType;
}

export const LIVE_LITE_PAGE_SIZE = 20;

/*
 * Socket batches can arrive every second on a busy day. Each one asks about the events it named, so they
 * are spaced out: the first goes after this long at most, and the ones in between are folded into it.
 */
const REFRESH_SPACING_MS = 5000;

const SOCKET_SCOPE = 'live-lite';

const isShown = (event: LiveLiteEvent | null): event is LiveLiteEvent => {
  const { environment } = store.getState();

  return !!event?.eid && !environment.DEMO_CUSTOMER_IDS.includes(Number(event.client?.cid));
};

/* The order the query asks for, kept when the socket merges an event into a list already on screen. */
const byStartTime = (a: LiveLiteEvent, b: LiveLiteEvent): number =>
  (a.startTime ?? '').localeCompare(b.startTime ?? '');

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useLiveLiteEvents = ({ search, eventType }: LiveLiteFilters) => {
  const client = useApolloClient();
  const socket = useEventsSocket();
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState<LiveLiteEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  /*
   * How many live events there are in all, which is more than the page shows. It starts from the query
   * and is then kept up by the socket merges — they never re-run the query, so reading it straight from
   * the response left the heading stuck at whatever it said when the page opened.
   */
  const [total, setTotal] = useState<number>();

  const input = useMemo(() => {
    const { input: filters } = transformFiltersToVariables({
      sortBy: 'startTime',
      sortOrder: 'ASC',
      search,
      eventType,
      limit: LIVE_LITE_PAGE_SIZE
    });

    return { ...filters, status: Event.Phase.Live };
  }, [search, eventType]);

  /* Value, not identity: the filters are rebuilt every render. */
  const inputKey = JSON.stringify(input);

  /* A filter change starts a new list rather than appending to the one built with the old filters. */
  useEffect(() => {
    setPage(1);
    setEvents([]);
    setLoaded(false);
    setTotal(undefined);
  }, [inputKey]);

  /*
   * `no-cache`: `getEvents`' cache policy merges every query that shares its filters into one entry, so
   * this page's rows would mix with the full events list's. The pages are accumulated here instead.
   */
  const { data, loading, error, refetch } = useQuery<LiveLiteEventsQuery, LiveLiteEventsVariables>(QUERY, {
    variables: { input: { ...input, page } },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    const response = data?.getEvents;

    if (!response) {
      return;
    }

    const rows = response.result.filter(isShown);

    setEvents((current) => {
      /* Page 1 is also what a refetch returns, so it replaces rather than duplicates. */
      if ((response.page ?? 1) <= 1) {
        return rows;
      }

      const seen = new Set(current.map((event) => event.eid));

      return [...current, ...rows.filter((event) => !seen.has(event.eid))];
    });

    setTotal(response.total ?? undefined);
    setLoaded(true);
  }, [data]);

  const hasMore = total === undefined ? !!data?.getEvents?.has_more : page * LIVE_LITE_PAGE_SIZE < total;

  /* The merge reads the list it is merging into, and must not do that from inside a state updater. */
  const latestEvents = useRef(events);

  latestEvents.current = events;

  /*
   * What the socket reported, merged into the rows already on screen: an event that still matches is
   * updated or added, one that no longer comes back has stopped being live and is dropped. The full
   * events list does the same through the Apollo cache; here the list is local, so it is done by hand.
   *
   * Only the named events are asked about — one request per batch, whatever the length of the list.
   */
  const mergeChanged = useCallback(
    async (ids: number[]): Promise<void> => {
      const { data: changed } = await client.query<LiveLiteEventsQuery, LiveLiteEventsVariables>({
        query: QUERY,
        variables: { input: { ...input, ids, ignore_pagination: true, page: 1 } },
        fetchPolicy: 'no-cache'
      });

      const rows = (changed.getEvents?.result ?? []).filter(isShown);
      const found = new Map(rows.map((event) => [event.eid, event]));
      const asked = new Set(ids.map(String));

      const { current } = latestEvents;

      const kept = current
        .filter((event) => !asked.has(event.eid) || found.has(event.eid))
        .map((event) => found.get(event.eid) ?? event);

      const known = new Set(kept.map((event) => event.eid));
      const next = [...kept, ...rows.filter((event) => !known.has(event.eid))].sort(byStartTime);

      setEvents(next);

      /* The heading counts every live event, not only the ones on screen, so it moves by the same delta. */
      const delta = next.length - current.length;

      if (delta !== 0) {
        setTotal((current_) => (current_ === undefined ? current_ : Math.max(0, current_ + delta)));
      }
    },
    [client, input]
  );

  const latestMerge = useRef(mergeChanged);

  latestMerge.current = mergeChanged;

  const latestRefetch = useRef(refetch);

  latestRefetch.current = refetch;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastRun = 0;
    let pending = new Set<number>();

    const schedule = (): void => {
      if (timer !== undefined) {
        return;
      }

      timer = setTimeout(() => {
        timer = undefined;
        lastRun = Date.now();

        const batch = [...pending];

        pending = new Set();

        /* A resync says only that something was missed, so the list is rebuilt from page 1. */
        if (batch.length) {
          void latestMerge.current(batch);
        } else {
          void latestRefetch.current();
        }
      }, Math.max(0, lastRun + REFRESH_SPACING_MS - Date.now()));
    };

    /* The query is `no-cache`, so mounting already fetches fresh rows; this only clears the flag. */
    socket.consumeDirty(SOCKET_SCOPE);

    const unsubscribe = socket.subscribe(SOCKET_SCOPE, (message) => {
      if (message.kind === 'events') {
        message.ids.forEach((id) => pending.add(id));

        /* A deletion needs no request: drop the row and move on. */
        if (message.removedIds.length) {
          const gone = new Set(message.removedIds.map(String));
          const dropped = latestEvents.current.filter((event) => gone.has(event.eid)).length;

          if (dropped) {
            setEvents((current) => current.filter((event) => !gone.has(event.eid)));
            setTotal((current) => (current === undefined ? current : Math.max(0, current - dropped)));
          }
        }

        if (message.ids.length) {
          schedule();
        }
      } else if (message.kind === 'resync') {
        schedule();
      }
    });

    return (): void => {
      unsubscribe();

      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [socket, inputKey]);

  return {
    events,
    error,
    refetch,
    total,
    hasMore,
    loaded,
    loading,
    loadMore: useCallback((): void => {
      if (hasMore && !loading) {
        setPage((current) => current + 1);
      }
    }, [hasMore, loading])
  };
};
